import {
  AHRoutineIcon,
  AHText,
  Box,
  Space,
  TouchableBox,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { useState } from "react";
import { View } from "react-native";

import { DateTime } from "luxon";
import { LinearGradient } from "expo-linear-gradient";
import { graphql, useFragment } from "react-relay";

import { KPIItemInProgress } from "./KPIItemTeamInProgress";
import * as d from "d3-scale";

import * as shape from "d3-shape";
import Svg, { Path } from "react-native-svg";
import { KPIItemTeam_Routine$key } from "./__generated__/KPIItemTeam_Routine.graphql";

const base_height = 100;
export const KPIItem = (props: Props) => {
  const [width, setWidth] = useState<number | null>(null);
  return (
    <View
      onLayout={(e) => {
        setWidth(e.nativeEvent.layout.width);
      }}
      style={{ flex: 1 }}
    >
      {width !== null && width > 1 ? (
        <InnerKPIItem {...props} width={width} />
      ) : null}
    </View>
  );
};

type Props = {
  routine: KPIItemTeam_Routine$key;
  onPress: (
    routineId: string,
    routineTitle: string,
    currentSequenceNumber: number
  ) => void;
  spaceTop?: Space;
  spaceLeft?: Space;
  spaceRight?: Space;
};

function InnerKPIItem(props: Props & { width: number }) {
  const routine = useFragment(
    graphql`
      fragment KPIItemTeam_Routine on Routine {
        id
        title
        icon
        schedule {
          rules {
            freq
          }
        }
        occasions(first: 7) {
          edges {
            node {
              id
              sequenceNumber
              startAt
              endAt
              ...KPIItemTeamInProgress_RoutineOccasion

              occasionSections(teamId: $teamId) {
                id
                section {
                  id
                  assignedTeam {
                    id
                    color
                  }
                }
                taskExecutions {
                  ... on TaskExecutionNumber {
                    id
                    isCompleted
                    numberValue
                    goals {
                      greaterThan
                      lessThan
                    }
                    formattedNumberValue
                  }
                }
              }
            }
          }
        }
      }
    `,
    props.routine
  );
  const theme = useTheme();

  const history =
    routine?.occasions?.edges
      ?.map((edge) => {
        const total = edge?.node?.occasionSections
          .map((section) =>
            section?.taskExecutions && section.taskExecutions.length > 0
              ? section.taskExecutions[0].numberValue
              : undefined
          )
          .reduce((acc, cur) => {
            if (cur === null || cur === undefined) return acc;
            return cur + (acc ?? 0);
          }, null);

        return {
          sId: edge.node.id,
          date: DateTime.fromISO(edge.node.startAt).toJSDate(),
          value: total,
        };
      })
      .filter(({ value }) => value !== null) ?? [];

  const goalValues = routine.occasions.edges
    .map((edge) => {
      const totalGoal = edge?.node?.occasionSections?.reduce<null | number>(
        (prev, s) => {
          const te = s.taskExecutions[0];
          if (!te) {
            return prev;
          }
          const goal = te.goals?.greaterThan ?? te.goals?.lessThan;
          if (goal === undefined || goal === null) {
            return prev;
          }

          return (
            (te.goals?.greaterThan ?? te.goals?.lessThan ?? 0) + (prev ?? 0)
          );
        },
        null
      );
      return {
        id: edge.node.id,
        value: totalGoal,
        date: new Date(edge.node.startAt),
      };
    })
    .filter(({ value }) => value !== null);

  const endDate = history[history.length - 1]?.date ?? new Date();
  const startDate = history[0]?.date ?? new Date();

  const layout = useLayoutContext();
  const width = props.width * 0.6;
  const height = base_height - layout.space.small * 2;

  const scaleY = d
    .scaleLinear()
    .domain([
      Math.max(
        ...history.map((h) => h.value ?? 0),
        ...goalValues.map((v) => v.value ?? 0)
      ) * 1.05,
      Math.min(0, ...history.map((h) => h.value ?? 0)) * 0.9,
    ])
    .range([0, height]);

  const scaleX = d.scaleTime([endDate, startDate], [0, width]);

  const valueLine = shape
    .line<(typeof history)[0]>()
    .curve(shape.curveBasis)
    .x((d) => scaleX(d.date))
    .y((d) => (d.value ? scaleY(d.value) : 0))(history);

  const goalLine = shape
    .line<(typeof goalValues)[0]>()
    .curve(shape.curveStep)
    .x((d) => scaleX(d.date))
    .y((d) => (d.value ? scaleY(d.value) : 0))(goalValues);

  return (
    <TouchableBox
      color="surface-primary"
      borderColor="border-primary"
      onPress={() => {
        if (routine.occasions.edges.length > 0) {
          props.onPress(
            routine.id,
            routine.title,
            routine.occasions.edges[0].node.sequenceNumber
          );
        }
      }}
      cornerRadius="large"
      style={{
        height: base_height,
        flexDirection: "row",
        overflow: "hidden",
        borderWidth: 1,
      }}
      spaceRight="small"
    >
      <Box
        style={{
          flex: 1,
        }}
        pointerEvents="none"
        spaceVertical="small"
      >
        <Svg
          style={{ flex: 1, alignSelf: "flex-end" }}
          pointerEvents="none"
          width={width}
          height={height}
        >
          {goalLine ? (
            <Path
              d={goalLine}
              stroke={theme.negative}
              strokeWidth={2}
              fill="none"
              disabled
            />
          ) : null}

          {valueLine ? (
            <Path
              d={valueLine}
              stroke="#000"
              strokeWidth={2}
              fill="none"
              disabled
            />
          ) : null}
        </Svg>
      </Box>
      <Box
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          top: 0,
          flexDirection: "row",
        }}
      >
        <Box
          style={{
            minWidth: props.width * 0.4,
            maxWidth: props.width * 0.65,
          }}
          spaceRight="xsmall"
          color="surface-primary"
        >
          <Box style={{ flex: 1 }} spaceVertical="small" spaceLeft="small">
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
                flex: 1,
              }}
            >
              <AHRoutineIcon size="small" type={routine.icon ?? "key"} />
              <AHText
                spaceLeft="small"
                style={{
                  flexShrink: 1,
                  flexGrow: 1,
                  flexBasis: "auto",
                }}
                numberOfLines={1}
                ellipsizeMode="tail"
                type="subtitle"
              >
                {routine.title}
              </AHText>
            </Box>
            {routine.occasions.edges.length > 0 ? (
              <KPIItemInProgress
                inProgress={routine.occasions.edges[0].node}
                freq={routine.schedule?.rules[0]?.freq}
              />
            ) : null}
          </Box>
        </Box>
        <LinearGradient
          style={{
            height: "100%",
            width: layout.space.medium,
          }}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          locations={[0, 1]}
          colors={[theme["surface-primary"], "rgba(255,255,255,0)"]}
        />
      </Box>
    </TouchableBox>
  );
}
